import { defineDoctype, normalizeLifecycleContract, type DoctypeCatalogMetadata } from "@kubdee/sdk";
import {
  dormitoryBillLines,
  dormitoryBillingPeriods,
  dormitoryBills,
  dormitoryBuildings,
  dormitoryLeases,
  dormitoryMeterReadings,
  dormitoryRooms,
  dormitorySettings,
  dormitoryTenants,
} from "./schemas";
import { DORMITORY_MODULE_ID } from "./constants";

const MASTER_CATALOG: DoctypeCatalogMetadata = {
  policy: {
    isSearchable: true,
    isExportable: true,
    isReportable: true,
    allowGenericCreate: true,
    allowGenericUpdate: true,
  },
};

const DOCUMENT_CATALOG: DoctypeCatalogMetadata = {
  policy: {
    isSearchable: true,
    isExportable: true,
    isReportable: true,
  },
};

const CHILD_TABLE_CATALOG = (parentDoctype: string, parentField: string): DoctypeCatalogMetadata => ({
  childTable: {
    parentDoctype,
    parentField,
    parentKeyField: "bill_id",
    sortField: "sort_order",
    cascadePolicy: "cascade",
  },
  policy: {
    isExportable: true,
    isReportable: true,
  },
});

export const leaseLifecycle = normalizeLifecycleContract({
  statusField: "status",
  docStatusField: null,
  draftStates: ["draft"],
  submittedStates: ["active"],
  cancelledStates: ["cancelled"],
  immutableStates: ["ended", "cancelled"],
  editAllowedStates: ["draft", "active"],
  actions: {
    submit: {
      label: "เริ่มสัญญา",
      from: ["draft"],
      to: "active",
      docStatus: null,
      reversalPolicy: "none",
      activityAction: "activated",
    },
    approve: {
      label: "จบสัญญา",
      from: ["active"],
      to: "ended",
      docStatus: null,
      reversalPolicy: "none",
      activityAction: "ended",
    },
    cancel: {
      label: "ยกเลิก",
      from: ["draft", "active"],
      to: "cancelled",
      docStatus: null,
      reversalPolicy: "status_only",
      activityAction: "cancelled",
    },
  },
});

export const billLifecycle = normalizeLifecycleContract({
  statusField: "status",
  docStatusField: null,
  draftStates: ["draft"],
  submittedStates: ["issued", "paid"],
  cancelledStates: ["cancelled"],
  immutableStates: ["paid", "cancelled"],
  editAllowedStates: ["draft"],
  actions: {
    submit: {
      label: "ออกบิล",
      from: ["draft"],
      to: "issued",
      docStatus: null,
      reversalPolicy: "none",
      activityAction: "issued",
    },
    approve: {
      label: "รับชำระ",
      from: ["issued"],
      to: "paid",
      docStatus: null,
      reversalPolicy: "none",
      activityAction: "paid",
    },
    cancel: {
      label: "ยกเลิก",
      from: ["draft", "issued"],
      to: "cancelled",
      docStatus: null,
      reversalPolicy: "status_only",
      activityAction: "cancelled",
    },
  },
});

export const buildingDoctype = defineDoctype({
  id: "building",
  moduleId: DORMITORY_MODULE_ID,
  table: dormitoryBuildings,
  label: "อาคารหอพัก",
  icon: "Building2",
  features: ["custom_fields", "permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: MASTER_CATALOG,
  displayField: "name",
  searchFields: ["code", "name"],
  columnLabels: {
    code: "รหัสอาคาร",
    name: "ชื่ออาคาร",
    branch_id: "สาขา",
    total_floors: "จำนวนชั้น",
    is_active: "เปิดใช้งาน",
  },
});

export const roomDoctype = defineDoctype({
  id: "room",
  moduleId: DORMITORY_MODULE_ID,
  table: dormitoryRooms,
  label: "ห้องพัก",
  icon: "DoorOpen",
  features: ["custom_fields", "permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: MASTER_CATALOG,
  displayField: "room_no",
  searchFields: ["room_no", "room_type", "status"],
  columnLabels: {
    building_id: "อาคาร",
    room_no: "เลขห้อง",
    floor_no: "ชั้น",
    room_type: "ประเภทห้อง",
    status: "สถานะ",
    rent_amount: "ค่าเช่า",
    deposit_amount: "เงินประกัน",
    water_meter_no: "เลขมิเตอร์น้ำ",
    electric_meter_no: "เลขมิเตอร์ไฟ",
  },
});

export const tenantDoctype = defineDoctype({
  id: "tenant",
  moduleId: DORMITORY_MODULE_ID,
  table: dormitoryTenants,
  label: "ผู้เช่า",
  icon: "UsersRound",
  features: ["naming", "custom_fields", "permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: MASTER_CATALOG,
  numberingField: "tenant_code",
  displayField: "full_name",
  searchFields: ["tenant_code", "full_name", "phone"],
  columnLabels: {
    tenant_code: "รหัสผู้เช่า",
    full_name: "ชื่อผู้เช่า",
    phone: "โทรศัพท์",
    email: "อีเมล",
    national_id: "เลขบัตรประชาชน",
    is_active: "เปิดใช้งาน",
  },
  defaultSeries: {
    prefix: "TN-{YYYY}-#####",
    resetEvery: "yearly",
    seriesName: "Standard",
  },
});

export const leaseDoctype = defineDoctype({
  id: "lease",
  moduleId: DORMITORY_MODULE_ID,
  table: dormitoryLeases,
  label: "สัญญาเช่า",
  icon: "FileSignature",
  features: ["naming", "custom_fields", "workflow", "print", "permissions", "automation", "list_view"],
  tier: 2,
  kind: "submittable",
  catalog: DOCUMENT_CATALOG,
  numberingField: "lease_no",
  displayField: "lease_no",
  searchFields: ["lease_no", "status"],
  columnLabels: {
    lease_no: "เลขที่สัญญา",
    room_id: "ห้องพัก",
    tenant_id: "ผู้เช่า",
    start_date: "วันที่เริ่ม",
    end_date: "วันที่สิ้นสุด",
    rent_amount: "ค่าเช่า",
    deposit_amount: "เงินประกัน",
    billing_day: "วันที่คิดรอบบิล",
    status: "สถานะ",
  },
  defaultSeries: {
    prefix: "LEASE-{YYYY}-#####",
    resetEvery: "yearly",
    seriesName: "Standard",
  },
  lifecycle: leaseLifecycle,
});

export const billingPeriodDoctype = defineDoctype({
  id: "billing_period",
  moduleId: DORMITORY_MODULE_ID,
  table: dormitoryBillingPeriods,
  label: "รอบบิลหอพัก",
  icon: "CalendarClock",
  features: ["naming", "custom_fields", "permissions", "automation", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: DOCUMENT_CATALOG,
  numberingField: "period_no",
  displayField: "name",
  searchFields: ["period_no", "name", "period_month", "status"],
  columnLabels: {
    period_no: "เลขที่รอบบิล",
    name: "ชื่อรอบบิล",
    period_month: "เดือนรอบบิล",
    start_date: "วันที่เริ่ม",
    end_date: "วันที่สิ้นสุด",
    due_date: "วันครบกำหนด",
    status: "สถานะ",
  },
  defaultSeries: {
    prefix: "DORM-PER-{YYYY}{MM}-####",
    resetEvery: "monthly",
    seriesName: "Standard",
  },
});

export const meterReadingDoctype = defineDoctype({
  id: "meter_reading",
  moduleId: DORMITORY_MODULE_ID,
  table: dormitoryMeterReadings,
  label: "บันทึกมิเตอร์",
  icon: "Gauge",
  features: ["custom_fields", "permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: DOCUMENT_CATALOG,
  displayField: "meter_type",
  searchFields: ["meter_type", "status"],
  columnLabels: {
    billing_period_id: "รอบบิล",
    room_id: "ห้องพัก",
    lease_id: "สัญญา",
    meter_type: "ประเภทมิเตอร์",
    previous_reading: "เลขครั้งก่อน",
    current_reading: "เลขปัจจุบัน",
    usage: "หน่วยที่ใช้",
    unit_rate: "ราคาต่อหน่วย",
    amount: "ยอดเงิน",
    reading_date: "วันที่จด",
    status: "สถานะ",
  },
});

export const billDoctype = defineDoctype({
  id: "bill",
  moduleId: DORMITORY_MODULE_ID,
  table: dormitoryBills,
  label: "บิลค่าเช่า",
  icon: "ReceiptText",
  features: ["naming", "custom_fields", "workflow", "print", "permissions", "automation", "list_view"],
  tier: 2,
  kind: "submittable",
  catalog: DOCUMENT_CATALOG,
  numberingField: "bill_no",
  displayField: "bill_no",
  searchFields: ["bill_no", "status"],
  columnLabels: {
    bill_no: "เลขที่บิล",
    billing_period_id: "รอบบิล",
    lease_id: "สัญญา",
    room_id: "ห้องพัก",
    tenant_id: "ผู้เช่า",
    bill_date: "วันที่ออกบิล",
    due_date: "วันครบกำหนด",
    rent_amount: "ค่าเช่า",
    water_amount: "ค่าน้ำ",
    electric_amount: "ค่าไฟ",
    service_amount: "ค่าบริการ",
    discount_amount: "ส่วนลด",
    late_fee_amount: "ค่าปรับ",
    total_amount: "ยอดรวม",
    paid_amount: "ยอดชำระ",
    status: "สถานะ",
  },
  defaultSeries: {
    prefix: "DORM-BILL-{YYYY}{MM}-####",
    resetEvery: "monthly",
    seriesName: "Standard",
  },
  lifecycle: billLifecycle,
});

export const billLineDoctype = defineDoctype({
  id: "bill_line",
  moduleId: DORMITORY_MODULE_ID,
  table: dormitoryBillLines,
  label: "รายการบิลค่าเช่า",
  icon: "ListChecks",
  features: ["custom_fields", "permissions"],
  tier: 2,
  kind: "child_table",
  catalog: CHILD_TABLE_CATALOG("bill", "lines"),
  displayField: "description",
  searchFields: ["description", "line_type"],
  columnLabels: {
    bill_id: "บิล",
    line_type: "ประเภทรายการ",
    description: "รายละเอียด",
    quantity: "จำนวน",
    unit_rate: "ราคาต่อหน่วย",
    amount: "ยอดเงิน",
    sort_order: "ลำดับ",
  },
});

export const settingsDoctype = defineDoctype({
  id: "dormitory_settings",
  moduleId: DORMITORY_MODULE_ID,
  table: dormitorySettings,
  label: "ตั้งค่าหอพัก",
  icon: "Settings",
  features: ["custom_fields", "permissions"],
  tier: 2,
  kind: "single",
  catalog: {
    policy: {
      isSearchable: false,
      isExportable: true,
      isReportable: false,
    },
  },
  displayField: null,
  searchFields: null,
  columnLabels: {
    default_rent_amount: "ค่าเช่าเริ่มต้น",
    default_water_rate: "ค่าน้ำต่อหน่วย",
    default_electric_rate: "ค่าไฟต่อหน่วย",
    default_service_charge: "ค่าบริการเริ่มต้น",
    default_due_day: "วันครบกำหนด",
    grace_period_days: "วันผ่อนผัน",
    rent_charge_timing: "รูปแบบเก็บค่าเช่า",
    billing_cycle_start_day: "วันเริ่มรอบบิล",
    invoice_issue_day: "วันออกบิล",
    prorate_policy: "วิธีคิดค่าเช่ากลางรอบ",
    utility_billing_mode: "วิธีคิดค่าน้ำค่าไฟ",
    advance_rent_months: "ค่าเช่าล่วงหน้า",
    deposit_months: "เงินประกัน",
    late_fee_type: "รูปแบบค่าปรับ",
    late_fee_amount: "ค่าปรับ",
  },
});

export const dormitoryDoctypes = [
  buildingDoctype,
  roomDoctype,
  tenantDoctype,
  leaseDoctype,
  billingPeriodDoctype,
  meterReadingDoctype,
  billDoctype,
  billLineDoctype,
  settingsDoctype,
] as const;
